import * as React from "react";
import { CardActionArea, Chip, Rating, Stack, Typography } from "@mui/material";
import { XArticleContext } from "../../../context/XArticleProvider";

import { useBreakpoint } from "../../../hooks/useBreakpoint";
import { useXoraiaValue } from "../../../hooks/useXoraia";
import { USE_SELECTED_INDEX } from "../../../hooks/useSelectedElement";
import { useSetRecoilState } from "recoil";
import { getValueOnBreakpoint } from "../../../lib/getValueOnBreakpoint";
import { clickHandler } from "../../../lib/clickHandler";

import { isEqual } from "lodash";

import { generateSide } from "../../../lib/generateSide";
import {
  IXArticleProps,
  XArticleProps,
} from "../../../utils/interfaces/common/XArticle.interfaces";
import { useXCommon } from "../../../hooks/useXCommon";
import { useMount } from "react-use";
import { generateRadius } from "../../../lib/generateRadius";
import { generateBorder } from "../../../lib/generateBorder";
import {
  MAIN_COLOR,
  SECONDARY_COLOR,
} from "../../../utils/constants/common/utils";
import { DEFAULT_FONT_FAMILY } from "../../../utils/constants/common/fonts";
import AddShoppingCartBtn from "../../core/dynamic/AddShoppingCartBtn";
import AddToWishListBtn from "../../core/dynamic/AddToWishListBtn";

const compareProps = (prevProps: any, nextProps: any) => {
  return isEqual(prevProps, nextProps);
};

const XArticle: React.FC<XArticleProps> = React.memo(
  ({ initData = { all: {} }, index }) => {
    const articleData = React.useContext(XArticleContext);

    // WE TRACK THE BREAK POINTS CHANGES
    const breakpoint = useBreakpoint();

    const { env, imageComponent, Link } = useXoraiaValue();

    // WE ARE GETTING THE CURRENT ELEMENT FROM THE ATOM FAMILY

    const { xCommon, setXCommon } = useXCommon("ARTICLE");

    useMount(() => {
      if (!xCommon) {
        setXCommon({
          name: "XArticle",
          childOf: null,
          elementsIndexes: [],
          hasChildren: true,
          hierachy: "XArticle",
          index: "ARTICLE",
          value: initData,
        });
      }
    });

    // GARBING THE INDEX UPDATER FOR GENERAL USAGE
    const setSelectedElement = useSetRecoilState(USE_SELECTED_INDEX);

    // IF WE DONT HAVE THIS ELEMENT CONFIGS ADDED WE DON'T SHOW IT
    const devProps = {
      id: "ARTICLE" + index,
      onClick: (e: any) =>
        clickHandler(e, setSelectedElement, "ARTICLE" + index),
    };

    if (!xCommon) return null;

    const { value } = xCommon;

    // WE NOW USE THE CURRENT BREAKPOINT VALUE ON THIS ELEMENT
    const valueAtBreakPoint = getValueOnBreakpoint<IXArticleProps>(
      value,
      breakpoint
    );

    const {
      padding,
      margin,
      bgColor,

      // image
      imageHeight,
      imageRadius,
      imageTransition = "fade",

      // badges
      badgePosition = { Top: "15px", Left: "15px" },
      badgeColor = "#262626",
      badgeWidth,
      badgeHeight = "21px",
      badgeTextColor = "#FFFFFF",
      badgeRadius,
      badgeFontWeight = 600,
      badgeFontSize = "12px",

      // BTN GROUP
      btnGroupTransition, // TODO IMPLEMENT USING ANIMATE.CSS ON HOVER
      btnGroupDirection = "column",
      btnGroupPosition = { Top: "15px", Right: "15px" },
      btnGroupColor,
      btnGroupGap = "10px",
      btnGroupMargin,
      btnGroupPadding,
      btnGroupHoverPosition,

      // BTN PROPS
      btnWidth,
      btnHeight,
      btnColor = "#FFFFFF",
      btnTextColor = "#000000",
      btnRadius = {
        TopLeft: "50%",
        TopRight: "50%",
        BottomLeft: "50%",
        BottomRight: "50%",
      },
      btnBorder = { width: "1px", color: "#DDDDDD" },
      btnHoverColor = "#000000",
      btnHoverTextColor = "#FFFFFF",
      btnHoverRadius,
      btnHoverBorder = { width: "1px", color: "#000000" },

      // CONTENT
      contentPadding = { Top: "15px", Bottom: "15px" },
      contentColor,
      contentPosition = "center",
      contentGap = "5px",

      // CATEGORY
      categoryColor = "#999999",
      categoryFontFamily = DEFAULT_FONT_FAMILY,
      categoryFontSize = "12px",
      categoryLetterSpacing,
      categoryFontWeight,
      categoryHoverColor = "#000000",

      // NAME
      nameColor = "#666666",
      nameFontFamily = DEFAULT_FONT_FAMILY,
      nameFontSize = "14px",
      nameLetterSpacing,
      nameFontWeight = 600,
      nameHoverColor = MAIN_COLOR,

      // PRICE
      priceColor = "#000000",
      priceFontFamily = DEFAULT_FONT_FAMILY,
      priceFontSize = "16px",
      priceLetterSpacing,
      priceFontWeight = 800,

      // DISCOUNT
      discountColor = "#999999",
      discountFontFamily = DEFAULT_FONT_FAMILY,
      discountFontSize = "14px",
      discountLetterSpacing,
      discountFontWeight,
      discountTextDecoration = "line-through",

      //RATING
      ratingColor = SECONDARY_COLOR,
      // rest
      ...sx
    } = valueAtBreakPoint;

    const images = articleData.images.filter((_, i) =>
      imageTransition === "fade" ? i < 2 : i < 1
    );

    return (
      <Stack
        {...(env === "dev" && devProps)}
        position="relative"
        boxSizing="border-box"
        sx={{
          ...(sx as any),
          margin: generateSide("margin", margin),
          padding: generateSide("padding", padding),
          bgcolor: bgColor,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          zIndex: 5,

          "& a": { textDecoration: "none" },

          "&[data-x-selected]": {
            "& figure": {
              zIndex: -1,
            },
          },

          "& .MuiFab-root": { opacity: 0 },

          // CUSTOM STYLING
          "& figure": {
            position: "relative",
            overflow: "hidden",
            width: "100%",
            height: imageHeight ?? "350px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            m: 0,

            "& img": {
              position: "absolute",
              transition: "linear all 0.25s",
              zIndex: 0,
              top: 0,
              left: 0,

              "&:first-of-type": {
                opacity: 1,
              },

              "&:last-of-type:not(:first-of-type)": {
                opacity: 0,
                // zIndex: 1,
              },
            },
          },

          //  Hover
          "&:hover": {
            "& .FAB_GROUP": {
              ...generateSide("position", btnGroupHoverPosition),
            },

            "& .MuiFab-root": { opacity: 1 },

            "& figure img": {
              "&:first-of-type": {
                transform: () =>
                  imageTransition === "zoom_out" ? "scale(1.2)" : "none",
              },

              "&:last-of-type:not(:first-of-type)": {
                opacity: 1,
              },
            },
          },
        }}
      >
        <Stack component="figure">
          <CardActionArea
            sx={{ height: "100%" }}
            LinkComponent={Link}
            href={"/products/" + articleData.id}
          >
            {/* IMAGE VIEW  */}
            {imageComponent &&
              images.length > 0 &&
              images.map((img, i) => {
                return imageComponent(
                  {
                    src: img,
                    alt: articleData.name,
                    objectFit: "cover",
                    borderRadius: imageRadius,
                    height: imageHeight ?? "350px",
                    width: "100%",
                  },
                  env ?? "dev",
                  {},
                  i.toString()
                );
              })}

            {/* BADGE VIEW  */}
            {articleData.badge && (
              <Chip
                size="small"
                label={articleData.badge.name}
                sx={{
                  position: "absolute",
                  zIndex: 2,
                  ...generateSide("position", badgePosition),
                  bgcolor: badgeColor,
                  width: badgeWidth,
                  height: badgeHeight,
                  color: badgeTextColor,
                  borderRadius: generateRadius(badgeRadius),
                  fontWeight: badgeFontWeight,
                  fontSize: badgeFontSize,
                }}
              />
            )}
          </CardActionArea>
          {/* BUTTONS GROUP  */}
          <Stack
            className="FAB_GROUP"
            direction={btnGroupDirection}
            spacing={btnGroupGap}
            sx={{
              transition: "all linear 0.25s",
              position: "absolute",
              zIndex: 2,
              ...generateSide("position", btnGroupPosition),
              bgcolor: btnGroupColor,
              marging: generateSide("margin", btnGroupMargin),
              padding: generateSide("padding", btnGroupPadding),

              "& .MuiFab-root": {
                transition: "all linear 0.25s",
                boxShadow: "none",
                width: btnWidth,
                height: btnHeight,
                bgcolor: btnColor,
                color: btnTextColor,
                borderRadius: generateRadius(btnRadius),
                border: generateBorder(btnBorder),

                "&:hover, &.isActived": {
                  bgcolor: btnHoverColor,
                  color: btnHoverTextColor,
                  borderRadius: generateRadius(btnHoverRadius, "50%"),
                  border: generateBorder(btnHoverBorder),
                },
              },
            }}
          >
            <AddToWishListBtn />
            <AddShoppingCartBtn />
          </Stack>
        </Stack>
        <Stack
          spacing={contentGap}
          sx={{
            padding: generateSide("padding", contentPadding),
            bgcolor: contentColor,
            justifyContent: contentPosition,
            // textAlign: "center",
            "& .MuiTypography-body1": {
              display: "flex",
              alignItems: "center",
              justifyContent: contentPosition,
            },
          }}
        >
          {articleData.category && (
            <Typography
              component={Link}
              href={"/shop?c=" + articleData.category.id}
              sx={{
                color: categoryColor,
                fontFamily: categoryFontFamily,
                fontSize: categoryFontSize,
                letterSpacing: categoryLetterSpacing,
                fontWeight: categoryFontWeight,
                lineHeight: "1em",
                width: "auto",
                "&:hover": {
                  color: categoryHoverColor,
                  cursor: "pointer",
                },
              }}
            >
              {articleData.category.name}
            </Typography>
          )}

          {articleData.name && (
            <Typography
              component={Link}
              href={"/products/" + articleData.id}
              sx={{
                color: nameColor,
                fontFamily: nameFontFamily,
                fontSize: nameFontSize,
                letterSpacing: nameLetterSpacing,
                fontWeight: nameFontWeight,
                lineHeight: "1em",
                width: "auto",
                "&:hover": {
                  color: nameHoverColor,
                  cursor: "pointer",
                },
              }}
            >
              {articleData.name}
            </Typography>
          )}

          <Rating
            value={articleData.initialRate ?? articleData.rate ?? 0}
            readOnly
            precision={0.5}
            size="small"
            sx={{ color: ratingColor, justifyContent: contentPosition }}
          />

          {articleData.price && (
            <Typography
              sx={{
                color: priceColor,
                fontFamily: priceFontFamily,
                fontSize: priceFontSize,
                letterSpacing: priceLetterSpacing,
                fontWeight: priceFontWeight,
                lineHeight: "1em",
                width: "auto",
              }}
            >
              {(articleData.discount
                ? articleData.price - articleData.discount
                : articleData.price) + " F CFA"}
              {articleData.discount && (
                <Typography
                  component="span"
                  sx={{
                    ml: 1,
                    color: discountColor,
                    fontFamily: discountFontFamily,
                    fontSize: discountFontSize,
                    letterSpacing: discountLetterSpacing,
                    fontWeight: discountFontWeight,
                    textDecoration: discountTextDecoration,
                  }}
                >
                  {articleData.price} F CFA
                </Typography>
              )}
            </Typography>
          )}
        </Stack>
      </Stack>
    );
  },
  compareProps
);

export default XArticle;
